import { generatePath } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { Observable } from 'rxjs';

import { BaseClient } from './client';
import {
  ContentTypes,
  convertToProcessRequestBody,
  extractFileExtension,
} from './utils';

import {
  ConfirmPaymentRequest,
  FetchCertainPaymentMethodRequest,
  FetchCertainPaymentMethodResponse,
  FetchDirecta24OptionsRequest,
  FetchPaymentMethodsResponse,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
  RegisterPaymentRequest,
  UploadFileRequest,
} from '@cashier/cashier-api-model';

import {
  PaymentStateInfo,
  IntegrationSources,
  PaymentTypes,
} from '@cashier/cashier-model';

export interface CashierClientInterface {
  fetchPaymentMethods(
    type: PaymentTypes,
  ): Observable<FetchPaymentMethodsResponse>;

  processPayment(
    request: ProcessPaymentRequest,
  ): Observable<ProcessPaymentResponse>;

  registerPayment(
    request: RegisterPaymentRequest,
  ): Observable<ProcessPaymentResponse>;

  confirmPayment(
    request: ConfirmPaymentRequest,
  ): Observable<ProcessPaymentResponse>;

  fetchPaymentInfo(transactionId: string): Observable<any>;

  fetchCertainPaymentMethod(
    request: FetchCertainPaymentMethodRequest,
  ): Observable<FetchCertainPaymentMethodResponse>;

  fetchDirecta24Options(request: FetchDirecta24OptionsRequest): Observable<any>;

  uploadFile(request: UploadFileRequest): Observable<string>;
}

export class CashierClient
  extends BaseClient
  implements CashierClientInterface
{
  constructor(
    apiUrl: string,
    private acceptLanguage: string,
    private authorisationToken: string,
  ) {
    super(apiUrl);
  }

  private apiUrlList = {
    get: {
      cashierPaymentMethods: 'api/v1/cashier/payment-methods',
      paymentInfo: 'api/v1/cashier/payment',
    },
    post: {
      cashierMain: 'api/v1/cashier',
      externalProcessTransaction: 'api/v1/cashier/payment/process',
      registerPayment: 'api/v1/cashier/payment/register',
      uploadFile:
        '/api/v1/cashier/payment/:transactionId/document/:documentType',
    },
    put: {
      confirmRequest: 'api/v1/cashier/payment/confirm',
    },
  };

  fetchPaymentMethods(
    type: PaymentTypes,
  ): Observable<FetchPaymentMethodsResponse> {
    return this.get<FetchPaymentMethodsResponse>(
      `${this.apiUrlList.get.cashierPaymentMethods}/${type}`,
      null,
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
      },
    );
  }

  processPayment(
    request: ProcessPaymentRequest,
  ): Observable<ProcessPaymentResponse> {
    const requestUrlMap = {
      [IntegrationSources.Internal]: `${this.apiUrlList.post.cashierMain}/${request.vendor}/process`,
      [IntegrationSources.External]:
        this.apiUrlList.post.externalProcessTransaction,
    };

    return this.post(
      requestUrlMap[request.source],
      convertToProcessRequestBody(request),
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
        'Content-Type': 'application/json',
      },
    );
  }

  registerPayment({
    referenceId,
    type,
    walletId,
    sessionId,
    merchantId,
    registration,
  }: RegisterPaymentRequest): Observable<ProcessPaymentResponse> {
    return this.post(
      this.apiUrlList.post.registerPayment,
      {
        referenceId,
        type,
        walletId,
        sessionId,
        merchantId,
        registration,
      },
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
        'Content-Type': 'application/json',
      },
    );
  }

  fetchPaymentInfo(transactionId: string): Observable<PaymentStateInfo> {
    return this.get<PaymentStateInfo>(
      `${this.apiUrlList.get.paymentInfo}/${transactionId}`,
      null,
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
      },
    );
  }

  fetchCertainPaymentMethod({
    id,
    type,
    bonusCode,
  }: FetchCertainPaymentMethodRequest): Observable<FetchCertainPaymentMethodResponse> {
    return this.get<FetchCertainPaymentMethodResponse>(
      `${this.apiUrlList.get.cashierPaymentMethods}/${type}/${id}`,
      {
        bonusCode,
      },
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
      },
    );
  }

  confirmPayment({
    transactionId,
    paymentAttributes,
  }: ConfirmPaymentRequest): Observable<ProcessPaymentResponse> {
    return this.put<ProcessPaymentResponse>(
      `${this.apiUrlList.put.confirmRequest}/${transactionId}`,
      { paymentAttributes },
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
        'Content-Type': 'application/json',
      },
    );
  }

  fetchDirecta24Options({
    url,
    parameters,
  }: FetchDirecta24OptionsRequest): Observable<any> {
    return this.postWithoutApiUrl<any>(url, parameters);
  }

  uploadFile({
    data: { file },
    transactionId,
  }: UploadFileRequest): Observable<string> {
    const contentType = file.type as ContentTypes;

    if (!contentType) {
      Sentry.captureException(new Error('####Unsupported file type####:'), {
        extra: {
          fileType: file.type,
        },
      });
    }

    const documentType = extractFileExtension(contentType);

    return this.post(
      generatePath(this.apiUrlList.post.uploadFile, {
        transactionId,
        documentType,
      }),
      file,
      '',
      {
        'Accept-Language': this.acceptLanguage,
        'Pgw-Authorization': this.authorisationToken,
        'Content-Type': file.type,
      },
    );
  }
}
